/*
 * Copyright (C) 2022-2025 - Jeci SARL - https://jeci.fr
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import AlfrescoProxyService from "./AlfrescoProxyService";

/**
 * Service for communicating with the Pristy Diffusion backend (Alfresco webscripts proxy).
 * Handles site creation, templates, sync and CRUD operations.
 */
class DiffusionService extends AlfrescoProxyService {
  constructor() {
    super("DiffusionService");
  }

  /**
   * Check if diffusion integration is enabled.
   *
   * @returns {Promise<{enabled: boolean}>}
   */
  async getStatus() {
    return this.get("fr/jeci/pristy/diffusion/status", {
      enabled: false,
    });
  }

  /**
   * Get available diffusion templates.
   *
   * @returns {Promise<Array>} List of templates
   */
  async getTemplates() {
    return this.get("fr/jeci/pristy/diffusion/templates", []);
  }

  /**
   * Get diffusion sites for a workspace.
   *
   * @param {string} workspaceId - The workspace ID (site name)
   * @returns {Promise<Array>} List of diffusion sites for this workspace
   */
  async getSites(workspaceId) {
    return this.get(`fr/jeci/pristy/diffusion/sites/${workspaceId}`, []);
  }

  /**
   * Create a new diffusion site.
   *
   * @param {Object} payload - Site creation data
   * @returns {Promise<Object|null>} Created site or null on error
   */
  async createSite(payload) {
    return this.post("fr/jeci/pristy/diffusion/site", payload, {
      throwOnError: true,
    });
  }

  /**
   * Update an existing diffusion site.
   *
   * @param {Object} payload - Site update data (must include `id`)
   * @returns {Promise<Object|null>} Updated site or null on error
   */
  async updateSite(payload) {
    return this.post("fr/jeci/pristy/diffusion/site", payload, {
      throwOnError: true,
    });
  }

  /**
   * Delete a diffusion site.
   *
   * @param {string} siteId - The diffusion site ID
   * @returns {Promise<boolean>} True if deleted successfully
   */
  async deleteSite(siteId) {
    return this.delete(`fr/jeci/pristy/diffusion/site/${siteId}`);
  }

  /**
   * Trigger synchronization for a diffusion site.
   *
   * @param {string} siteId - The diffusion site ID
   * @param {string} [workspaceId] - The Alfresco site shortName (to grant sync account access)
   * @returns {Promise<Object|null>} Sync job details or null on error
   */
  async triggerSync(siteId, workspaceId) {
    const body = {};
    if (workspaceId) {
      body.workspace_id = workspaceId;
    }
    return this.post(`fr/jeci/pristy/diffusion/site/${siteId}/sync`, body, {
      throwOnError: true,
    });
  }
}

export default new DiffusionService();
